import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtPct, exportToPDF, exportToExcel } from "../lib/exports";
import { FileDown, FileSpreadsheet } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function EstadoResultados() {
  const [fechaIni, setFechaIni] = useState(monthStart());
  const [fechaFin, setFechaFin] = useState(today());
  const [sucursalId, setSucursalId] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/sucursales").then((r) => setSucursales(r.data)); }, []);

  const cargar = async () => {
    try {
      const params = { fecha_ini: fechaIni, fecha_fin: fechaFin };
      if (sucursalId) params.sucursal_id = sucursalId;
      const { data } = await api.get("/reportes/estado-resultados", { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { cargar(); }, []);

  const r = data?.resumen || {};

  const expPDF = () => {
    if (!data) return;
    exportToPDF({
      title: "Estado de Resultados", subtitle: `${data.fecha_ini} a ${data.fecha_fin}`,
      columns: [{ header: "Concepto", key: "concepto" }, { header: "Importe", key: "importe" }],
      rows: [
        { concepto: "Ventas", importe: fmtMoney(r.ventas) },
        { concepto: "(-) Costo de Venta", importe: fmtMoney(r.costo_venta) },
        { concepto: "= Utilidad Bruta", importe: fmtMoney(r.utilidad_bruta) },
        { concepto: "(-) Gastos Operativos", importe: fmtMoney(r.gastos_operativos) },
        { concepto: "= Utilidad Operativa", importe: fmtMoney(r.utilidad_operativa) },
        { concepto: "= Utilidad Neta", importe: fmtMoney(r.utilidad_neta) },
      ],
      summary: [
        { label: "Food Cost", value: fmtPct(r.food_cost_pct) },
        { label: "Margen Neto", value: fmtPct(r.margen_pct) },
      ],
    });
  };
  const expXLS = () => {
    if (!data) return;
    exportToExcel({ title: "Estado_Resultados", rows: [
      { Concepto: "Ventas", Importe: r.ventas },
      { Concepto: "Costo de Venta", Importe: r.costo_venta },
      { Concepto: "Utilidad Bruta", Importe: r.utilidad_bruta },
      { Concepto: "Gastos Operativos", Importe: r.gastos_operativos },
      { Concepto: "Utilidad Neta", Importe: r.utilidad_neta },
      { Concepto: "Margen %", Importe: r.margen_pct },
      { Concepto: "Food Cost %", Importe: r.food_cost_pct },
    ]});
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Análisis</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Estado de Resultados</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Desde"><Input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Hasta"><Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Sucursal">
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-end"><Button onClick={cargar} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Generar</Button></div>
      </Card>

      {data && (
        <Card className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cerebro Financiero</div>
              <h2 className="text-2xl font-bold tracking-tight mt-1">Estado de Resultados</h2>
              <div className="text-sm text-slate-500 mt-1">Periodo: {data.fecha_ini} al {data.fecha_fin}</div>
            </div>
            <div className="space-y-1 font-mono">
              <Line label="Ventas Totales" value={fmtMoney(r.ventas)} bold />
              <Line label="(-) Costo de Venta" value={fmtMoney(r.costo_venta)} />
              <div className="border-t border-slate-300 my-2" />
              <Line label="= UTILIDAD BRUTA" value={fmtMoney(r.utilidad_bruta)} bold positive={r.utilidad_bruta >= 0} highlight />
              <Line label="(-) Gastos Operativos" value={fmtMoney(r.gastos_operativos)} />
              <div className="border-t border-slate-300 my-2" />
              <Line label="= UTILIDAD OPERATIVA" value={fmtMoney(r.utilidad_operativa)} bold positive={r.utilidad_operativa >= 0} highlight />
              <div className="border-t-2 border-slate-900 my-3" />
              <Line label="= UTILIDAD NETA" value={fmtMoney(r.utilidad_neta)} bold positive={r.utilidad_neta >= 0} big />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-200">
              <div className="text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Food Cost</div>
                <div className="text-2xl font-bold font-mono mt-1">{fmtPct(r.food_cost_pct)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Margen Neto</div>
                <div className={`text-2xl font-bold font-mono mt-1 ${r.margen_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(r.margen_pct)}</div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Line = ({ label, value, bold, positive, highlight, big }) => (
  <div className={`flex justify-between items-center px-3 py-2 ${highlight ? "bg-slate-100" : ""} ${big ? "text-xl py-3" : ""}`}>
    <span className={`${bold ? "font-bold" : "text-slate-700"}`}>{label}</span>
    <span className={`${bold ? "font-bold" : ""} ${positive === true ? "text-emerald-600" : positive === false ? "text-red-600" : ""}`}>{value}</span>
  </div>
);
